import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Layout from '../components/Layout';

// const BASE_URL = 'http://localhost:5000';
const BASE_URL = 'http://192.168.0.57:5000';
const TYPE_OPTS        = [{ value: '1', label: 'Banner' }, { value: '2', label: 'Logo' }];
const BANNER_TYPE_OPTS = [{ value: '1', label: 'Car Banner' }, { value: '2', label: 'Transfer Banner' }];

const EMPTY_FORM = { banner_name: '', type: '1', banner_type: '1', bimage_alt: '' };

export default function ManageBanner() {
  const navigate = useNavigate();

  // ── Table ──
  const [data,       setData]       = useState([]);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total,      setTotal]      = useState(0);
  const [search,     setSearch]     = useState('');
  const [loading,    setLoading]    = useState(false);

  // ── Form (inline — same as PHP) ──
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [editId,     setEditId]     = useState(null);
  const [imageFile,  setImageFile]  = useState(null);
  const [preview,    setPreview]    = useState('');
  const [saving,     setSaving]     = useState(false);
  const fileRef = useRef();

  // ── Auth ──
  useEffect(() => { if (!localStorage.getItem('token')) navigate('/login'); }, []);

  // ── Fetch ──
  const fetchData = useCallback((pg = page, q = search) => {
    setLoading(true);
    API.get('/banners', { params: { page: pg, limit: 10, search: q } })
      .then(r => { setData(r.data.data); setTotalPages(r.data.totalPages); setTotal(r.data.total); })
      .catch(() => toast.error('Failed to load banners'))
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { fetchData(page, search); }, [page]);

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); fetchData(1, search); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // ── Form change ──
  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleFileChange = e => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Image must be under 2MB'); e.target.value = ''; return; }
    setImageFile(file);
    setPreview(URL.createObjectURL(file));
  };

  // ── Edit ──
  const handleEdit = (row) => {
    setEditId(row.banner_id);
    setForm({
      banner_name: row.banner_name || '',
      type:        String(row.type || '1'),
      banner_type: String(row.banner_type || '1'),
      bimage_alt:  row.bimage_alt || '',
    });
    setImageFile(null);
    const img = row.type == 2 ? row.logo_img : row.banner_img;
    const dir  = row.type == 2 ? 'img/logo' : 'img/banner';
    setPreview(img && img !== 'default.jpg' ? `${BASE_URL}/${dir}/${img}` : '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancel = () => { setForm(EMPTY_FORM); setEditId(null); setImageFile(null); setPreview(''); if (fileRef.current) fileRef.current.value = ''; };

  // ── Submit ──
  const handleSubmit = async () => {
    if (!form.banner_name.trim()) { toast.warning('Name is required'); return; }
    if (!editId && !imageFile)    { toast.warning('Image is required'); return; }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('banner_name', form.banner_name.trim());
      fd.append('type',        form.type);
      fd.append('banner_type', form.banner_type);
      fd.append('bimage_alt',  form.bimage_alt);
      if (imageFile) fd.append('banner_image', imageFile);

      if (editId) {
        await API.put(`/banners/${editId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Updated successfully');
      } else {
        await API.post('/banners', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Added successfully');
      }
      handleCancel();
      fetchData(page, search);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Operation failed');
    }
    setSaving(false);
  };

  // ── Status toggle ──
  const handleStatusToggle = async (row) => {
    const action = row.banner_status === 1 ? 'Deactivate' : 'Activate';
    if (!window.confirm(`${action} this banner?`)) return;
    try {
      const r = await API.patch(`/banners/${row.banner_id}/status`, { status: row.banner_status });
      toast.success(r.data.message);
      fetchData(page, search);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Status update failed');
    }
  };

  // ── Delete ──
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this banner?')) return;
    try {
      await API.delete(`/banners/${id}`);
      toast.success('Deleted successfully');
      fetchData(page, search);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  const imgSizeNote = form.type === '1'
    ? 'Banner size should be 554 × 404 px'
    : 'Logo size should be 188 × 100 px';

  const getImageUrl = (row) => {
    if (row.type == 2 && row.logo_img   && row.logo_img   !== 'default.jpg') return `${BASE_URL}/img/logo/${row.logo_img}`;
    if (row.type == 1 && row.banner_img && row.banner_img !== 'default.jpg') return `${BASE_URL}/img/banner/${row.banner_img}`;
    return null;
  };

  return (
    <Layout activePage="Manage Banner">
      <ToastContainer position="top-right" autoClose={3000} />

      <div style={S.header}>
        <h2 style={S.title}>Manage Banner / Logo</h2>
        <p style={S.subtitle}>Add and manage Banner /Logo</p>
      </div>

      {/* ── Inline Add / Edit Form ── */}
      <div style={S.formCard}>
        <div style={S.formCardHead}>
          <div>
            <h2 style={S.formTitle}>{editId ? '✏️ Edit Banner / Logo' : '➕ Add Banner / Logo'}</h2>
            {editId && <span style={S.editBadge}>Editing #{editId}</span>}
          </div>
          {editId && <button style={S.cancelBtn} onClick={handleCancel}>✕ Cancel Edit</button>}
        </div>

        <div style={S.formGrid}>
          {/* Name */}
          <div style={S.fg}>
            <label style={S.label}>Name <span style={S.req}>*</span></label>
            <input name="banner_name" value={form.banner_name} onChange={handleChange}
              placeholder="Enter banner name" style={S.input} maxLength={50} />
          </div>

          {/* Image Type */}
          <div style={S.fg}>
            <label style={S.label}>Image Type <span style={S.req}>*</span></label>
            <select name="type" value={form.type} onChange={handleChange} style={S.select}>
              {TYPE_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {/* Banner Type */}
          <div style={S.fg}>
            <label style={S.label}>Banner Type <span style={S.req}>*</span></label>
            <select name="banner_type" value={form.banner_type} onChange={handleChange} style={S.select}>
              {BANNER_TYPE_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {/* Alt Text (edit mode) */}
          {editId && (
            <div style={S.fg}>
              <label style={S.label}>Image Alt Text</label>
              <input name="bimage_alt" value={form.bimage_alt} onChange={handleChange}
                placeholder="Alt text for SEO" style={S.input} />
            </div>
          )}
        </div>

        {/* Image upload */}
        <div style={{ marginTop: 14 }}>
          <label style={S.label}>Image {!editId && <span style={S.req}>*</span>}</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
            <button type="button" style={S.fileBtn} onClick={() => fileRef.current.click()}>
              📎 {imageFile ? 'Change Image' : preview ? 'Replace Image' : 'Upload Image'}
            </button>
            <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.gif,.webp"
              style={{ display: 'none' }} onChange={handleFileChange} />
            {imageFile && <span style={{ fontSize: 11, color: '#1a9e5c' }}>✓ {imageFile.name}</span>}
            <span style={{ fontSize: 11, color: '#e07b00', fontWeight: 600 }}>⚠ {imgSizeNote}</span>
          </div>
          <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>
            JPG / PNG / GIF / WebP, max 2MB — image will be auto-resized
          </div>
          {preview && (
            <img src={preview} alt="Preview"
              style={{ marginTop: 10, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid #eee' }} />
          )}
        </div>

        {/* Submit */}
        <div style={{ marginTop: 18, display: 'flex', gap: 10 }}>
          {editId && <button style={S.cancelBtn} onClick={handleCancel}>Clear</button>}
          <button style={S.submitBtn} onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving…' : editId ? 'Update' : 'Add Banner'}
          </button>
        </div>
      </div>

      {/* ── Manage Table ── */}
      <div style={{ ...S.card, marginTop: 24 }}>
        <div style={S.tableHead}>
          <span style={S.tableTitle}>Manage Banner / Logo</span>
          <div style={S.searchWrap}>
            <span style={{ fontSize: 13, color: '#aaa' }}>🔍</span>
            <input type="text" placeholder="Search banner name..."
              value={search} onChange={e => setSearch(e.target.value)} style={S.searchInput} />
            {search && <button style={S.clearBtn} onClick={() => setSearch('')}>✕</button>}
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr style={S.theadRow}>
                <th style={S.th}>Name</th>
                <th style={S.th}>Banner Type</th>
                <th style={S.th}>Image Type</th>
                <th style={S.th}>Image</th>
                <th style={S.th}>Status</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={S.emptyCell}>Loading…</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={6} style={S.emptyCell}>No records available</td></tr>
              ) : data.map(row => {
                const imgUrl     = getImageUrl(row);
                const isActive   = row.banner_status === 1;
                const isEditing  = editId === row.banner_id;
                return (
                  <tr key={row.banner_id} style={{ ...S.tr, background: isEditing ? '#f3f2ff' : 'transparent' }}>
                    <td style={S.td}>
                      <span style={S.nameChip}>{row.banner_name}</span>
                    </td>
                    <td style={S.td}>
                      <span style={S.typeChip}>{row.banner_type == 1 ? 'Car Banner' : 'Transfer Banner'}</span>
                    </td>
                    <td style={S.td}>
                      <span style={{ ...S.imgTypeChip, background: row.type == 1 ? '#f0f8ff' : '#f3f2ff',
                        color: row.type == 1 ? '#1a6eb5' : '#6C63FF' }}>
                        {row.type == 1 ? '🖼️ Banner' : '🏷️ Logo'}
                      </span>
                    </td>
                    <td style={S.td}>
                      {imgUrl
                        ? <img src={imgUrl} alt={row.banner_name}
                            style={{ width: 100, height: 70, objectFit: 'cover', borderRadius: 6, border: '1px solid #eee' }} />
                        : <div style={S.noImg}>No Image</div>}
                    </td>
                    <td style={S.td}>
                      <button
                        onClick={() => handleStatusToggle(row)}
                        style={{
                          ...S.statusBtn,
                          background: isActive ? '#f0fff6' : '#fff0f0',
                          color:      isActive ? '#1a9e5c' : '#c0392b',
                          border:     `1px solid ${isActive ? '#b8f0d0' : '#fca5a5'}`,
                        }}>
                        {isActive ? '✅ Active' : '❌ Inactive'}
                      </button>
                    </td>
                    <td style={{ ...S.td, textAlign: 'right' }}>
                      <button style={S.editBtn} onClick={() => handleEdit(row)}>Edit</button>
                      <button style={S.delBtn}  onClick={() => handleDelete(row.banner_id)}>Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={S.pagination}>
          <span style={S.paginInfo}>
            {total > 0 ? `Showing ${(page-1)*10+1}–${Math.min(page*10, total)} of ${total}` : ''}
          </span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button style={S.pgBtn} disabled={page === 1} onClick={() => setPage(p => p-1)}>← Prev</button>
            <span style={S.paginInfo}>Page {page} / {totalPages || 1}</span>
            <button style={S.pgBtn} disabled={page === totalPages || totalPages === 0}
              onClick={() => setPage(p => p+1)}>Next →</button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

/* ─── Styles ─── */
const S = {
  formCard:     { background: '#fff', borderRadius: 16, padding: '24px', boxShadow: '0 2px 16px rgba(0,0,0,0.06)' },
  formCardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 10 },
  formTitle:    { margin: 0, fontSize: 20, fontWeight: 800, color: '#1a1d2e' },
  editBadge:    { fontSize: 11, fontWeight: 700, background: '#f3f2ff', color: '#6C63FF', borderRadius: 20, padding: '3px 10px', display: 'inline-block', marginTop: 4 },
 /* Header */
 header: { marginBottom: 20 },
 title: { margin: 0, fontSize: 26, fontWeight: 800, color: '#1a1d2e', letterSpacing: '-0.5px' },
 subtitle: { margin: '4px 0 0', fontSize: 13, color: '#999' },



  formGrid:     { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 },

  card:         { background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', overflow: 'hidden' },
  tableHead:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #f0f0f0', flexWrap: 'wrap', gap: 12 },
  tableTitle:   { fontSize: 14, fontWeight: 700, color: '#1a1d2e' },
  searchWrap:   { display: 'flex', alignItems: 'center', gap: 8, background: '#f8f8f8', border: '1px solid #e8e8e8', borderRadius: 10, padding: '7px 14px', minWidth: 220 },
  searchInput:  { border: 'none', outline: 'none', fontSize: 13, background: 'transparent', fontFamily: 'inherit', color: '#333', flex: 1 },
  clearBtn:     { background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: 12, padding: 0 },

  table:        { width: '100%', borderCollapse: 'collapse' },
  theadRow:     { background: '#fafafa' },
  th:           { padding: '12px 16px', fontSize: 11, fontWeight: 700, color: '#999', textAlign: 'left', borderBottom: '2px solid #f0f0f0', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  tr:           { transition: 'background 0.12s' },
  td:           { padding: '12px 16px', fontSize: 13, color: '#333', borderBottom: '1px solid #f8f8f8' },
  emptyCell:    { textAlign: 'center', padding: 40, color: '#bbb', fontSize: 14 },

  nameChip:     { background: '#f0fff6', color: '#1a9e5c', borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600 },
  typeChip:     { background: '#f0f8ff', color: '#1a6eb5', borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600 },
  imgTypeChip:  { borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600 },
  noImg:        { width: 100, height: 70, background: '#f3f2ff', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#aaa' },
  statusBtn:    { padding: '5px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' },
  editBtn:      { marginRight: 6, background: '#f3f2ff', color: '#6C63FF', border: '1px solid #d4d0ff', padding: '5px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' },
  delBtn:       { background: '#fff2f5', color: '#FF6584', border: '1px solid #ffd0d8', padding: '5px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' },

  pagination:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderTop: '1px solid #f0f0f0' },
  paginInfo:    { fontSize: 12, color: '#999' },
  pgBtn:        { padding: '5px 14px', background: '#f3f2ff', color: '#6C63FF', border: '1px solid #d4d0ff', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' },

  fg:           { display: 'flex', flexDirection: 'column', gap: 5 },
  label:        { fontSize: 12, fontWeight: 600, color: '#555' },
  req:          { color: '#ff6b6b' },
  input:        { padding: '9px 14px', border: '1.5px solid #e0e0e0', borderRadius: 10, fontSize: 13, fontFamily: 'inherit', color: '#333', outline: 'none', width: '100%', boxSizing: 'border-box' },
  select:       { padding: '9px 14px', border: '1.5px solid #e0e0e0', borderRadius: 10, fontSize: 13, fontFamily: 'inherit', color: '#333', outline: 'none', background: '#fff', width: '100%', cursor: 'pointer' },
  fileBtn:      { padding: '8px 16px', background: '#f3f2ff', color: '#6C63FF', border: '1px solid #d4d0ff', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' },
  cancelBtn:    { padding: '9px 18px', background: '#f5f5f5', color: '#666', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  submitBtn:    { padding: '10px 24px', background: 'linear-gradient(135deg,#6C63FF,#43BCCD)', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'inherit' },
};
